/**
 * SPOTOrdering (ClassAdjustments)
 *
 * REFACTORED VERSION - Uses SPOTOrderingContext for centralized state management.
 *
 * Architecture:
 * - SPOTOrderingProvider wraps the main content
 * - Child components use useSPOTOrdering() hook
 * - State flows through context, not props
 */

// Comment for merge

import React, { useEffect, useState, useMemo, useRef } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";

// Auth & Utils
import isAuthenticated from "../../../components/Utils/isAuthenticated";

// Components
import ClassAdjustTable from "./components/ClassAdjustTable";
import GuideDialog from "./components/GuideDialog";
import ChangelogDialog from "./components/ChangelogDialog";
import ClassAdjustAlert from "./components/ClassAdjustAlert";

// Icons
import MenuBookIcon from "@mui/icons-material/MenuBook";
import NewReleasesIcon from "@mui/icons-material/NewReleases";

// Hooks
import { useTermsQueryFull } from "../../../Hooks/useTermsQuery";
import { useDepartmentsQuery } from "../../../Hooks/useDepartmentsQuery";
import {
	useAllBlockItems,
	useBlockItemsQuery,
	useBlockIdsByDeptQuery,
} from "../../../Hooks/useBlockItemsQuery";
import { useAllItemsQuery } from "../../../Hooks/useAllItemsQuery";

// Context
import { SPOTOrderingProvider, useSPOTOrdering } from "../../../context";

// Atoms
import {
	selectedClassAtom,
	termsAtom,
	selectedDeptAtom,
	unsavedChangesAtom,
	combiningClassesArrayAtom,
	instanceRowsAtom,
	spotOrderingSelectionModelAtom,
} from "../../../components/Atoms/atoms";
import { createEmptyRowSelectionModel, getSelectedIdsFromModel } from "./components/selectionModel";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";

const HtmlTooltip = styled(({ className, ...props }) => (
	<Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: "#f5f5f9",
		color: "rgba(0, 0, 0, 0.87)",
		maxWidth: 220,
		fontSize: theme.typography.pxToRem(12),
		border: "1px solid #dadde9",
	},
}));

const UPDATE_NOTES_SESSION_KEY = "spot-ordering-tips-enabled";
const UPDATE_NOTES_EXPIRY = new Date("2026-04-11T00:00:00");

// ────────────────────────────────────────────────────────────────────────────────
// INNER COMPONENT (has access to context)
// ────────────────────────────────────────────────────────────────────────────────

function SPOTOrderingContent({
	isAdmin,
	departmentsList,
	handleDepartmentChange,
	isTermActive,
	blockIdsByDept,
	blockIdsFilteredTA,
	allBlockItems,
	loading: externalLoading,
}) {
	// Context - THE single source of truth for row data
	const { rows, displayRows, isLoading, updateRow, refreshData, updateClassMutation } =
		useSPOTOrdering();

	// Global atoms (still using Jotai for cross-component selection state)
	const [selectedClass, setSelectedClass] = useAtom(selectedClassAtom);
	const [unsavedChanges, setUnsavedChanges] = useAtom(unsavedChangesAtom);
	const [terms] = useAtom(termsAtom);
	const selectionModel = useAtomValue(spotOrderingSelectionModelAtom);
	const setSelectionModel = useSetAtom(spotOrderingSelectionModelAtom);
	const selectedBulkClassIds = useMemo(
		() => getSelectedIdsFromModel(selectionModel, displayRows),
		[selectionModel, displayRows],
	);

	// Local UI state
	const [openGuideDialog, setOpenGuideDialog] = useState(false);
	const [openChangelogDialog, setOpenChangelogDialog] = useState(false);
	const [alertState, setAlertState] = useState({
		open: false,
		status: null,
		message: null,
		type: null,
	});

	const [disabledUI, setDisabledUI] = useState(false);
	const [batchUpdated, setBatchUpdated] = useState(false);
	const [openSelectedClassModal, setOpenSelectedClassModal] = useState(false);
	const [shouldHighlightUpdates, setShouldHighlightUpdates] = useState(false);

	const [tipsEnabled, setTipsEnabled] = useState(false);

	// Combined loading state
	const loading = isLoading || externalLoading;

	useEffect(() => {
		if (!selectedClass || !openSelectedClassModal) {
			setUnsavedChanges(false);
		}
	}, [selectedClass, openSelectedClassModal, setUnsavedChanges]);

	// ─── ALERT EFFECTS ─────────────────────────────────────────────────────────

	useEffect(() => {
		if (selectedBulkClassIds.length > 0) {
			setAlertState({
				open: false,
				status: "info",
				message:
					"Entered multiple class editing/submission mode. Editing of individual courses is disabled.",
				type: "multiclass",
			});
		} else if (alertState.type === "multiclass") {
			setAlertState((prev) => ({ ...prev, open: false }));
		}
	}, [selectedBulkClassIds.length, alertState.type]);

	useEffect(() => {
		if (unsavedChanges) {
			setAlertState({
				open: true,
				status: "warning",
				message: "You have unsaved changes!",
				type: "unsaved",
			});
		} else {
			if (alertState.type === "unsaved") {
				setAlertState((prev) => ({ ...prev, open: false }));
			}
		}
	}, [unsavedChanges, alertState.type]);

	// ─── HANDLERS ──────────────────────────────────────────────────────────────

	const handleChangeBlkId = (event) => {
		const value = event.target.value;
		if (selectedClass) {
			updateRow(selectedClass.classid, { form: value });
			setSelectedClass((prev) => ({ ...prev, form: value }));
			setUnsavedChanges(true);
		}
	};

	const handleRefresh = () => {
		refreshData();
		setSelectedClass(null);
		setSelectionModel(createEmptyRowSelectionModel());
	};

	const [tooltipOpen, setTooltipOpen] = useState(false);

	useEffect(() => {
		if (typeof window === "undefined") {
			return undefined;
		}

		const autoTipsActive = new Date() < UPDATE_NOTES_EXPIRY;
		const storedTipsEnabled = window.sessionStorage.getItem(UPDATE_NOTES_SESSION_KEY);
		const nextTipsEnabled = autoTipsActive && storedTipsEnabled !== "false";

		setTipsEnabled(nextTipsEnabled);
		setShouldHighlightUpdates(nextTipsEnabled);
		setTooltipOpen(nextTipsEnabled);
		window.sessionStorage.setItem(UPDATE_NOTES_SESSION_KEY, nextTipsEnabled ? "true" : "false");

		return undefined;
	}, []);

	const handleTipsToggle = () => {
		const autoTipsActive = new Date() < UPDATE_NOTES_EXPIRY;
		const nextTipsEnabled = autoTipsActive ? !tipsEnabled : false;

		setTipsEnabled(nextTipsEnabled);
		setShouldHighlightUpdates(nextTipsEnabled);
		setTooltipOpen(nextTipsEnabled);
		window.sessionStorage.setItem(UPDATE_NOTES_SESSION_KEY, nextTipsEnabled ? "true" : "false");
	};

	const handleTooltipClose = () => {
		setTooltipOpen(false);
	};

	const handleTooltipOpen = () => {
		setTooltipOpen(true);
	};

	// ─── RENDER ────────────────────────────────────────────────────────────────

	return (
		<div className="flex flex-col items-end justify-end w-full relative   bg-digitalWhite ">
			{alertState.open && (
				<div className="fixed top-4 left-4  z-40">
					<ClassAdjustAlert
						variant="filled"
						severity={alertState.status}
						message={alertState.message}
					/>
				</div>
			)}

			<GuideDialog open={openGuideDialog} onClose={() => setOpenGuideDialog(false)} />
			<ChangelogDialog open={openChangelogDialog} onClose={() => setOpenChangelogDialog(false)} />

			<div className="flex flex-col w-full px-4  bg-digitalWhite ">
				<div className="flex items-center justify-end gap-4 py-2 w-full ">
					

					<div className="flex items-center gap-3">
						<HtmlTooltip
							open={tooltipOpen}
							placement="left"
							arrow
							onClose={handleTooltipClose}
							onOpen={handleTooltipOpen}
							title={
								<React.Fragment>
									<div className="flex flex-col">
										<div className="text-primary ">
											Check out our updated guide and change log for a full walkthrough of the new
											SPOT Ordering page and a summary of recent workflow changes.
										</div>
										{new Date() < UPDATE_NOTES_EXPIRY && (
											<button className="self-end mt-2 underline" onClick={handleTipsToggle}>
												{tipsEnabled ? "Disable Tip" : "Enable Tip"}
											</button>
										)}
									</div>
								</React.Fragment>
							}
						>
							<button
								className="inline-flex items-center gap-2 rounded-md border-2 border-slate-300 bg-white px-4 py-1.5 font-medium text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
								onClick={() => setOpenGuideDialog(true)}
							>
								*NEW* Guide <MenuBookIcon fontSize="small" />
							</button>
						</HtmlTooltip>
						<button
							className={`inline-flex items-center gap-2 rounded-md border-2 border-green-800 bg-green-800 px-4 py-1.5 font-medium text-white shadow-sm transition hover:bg-green-900 
								
							`}
							onClick={() => setOpenChangelogDialog(true)}
						>
							What&apos;s New <NewReleasesIcon fontSize="small" />
						</button>
					</div>
				</div>

				<ClassAdjustTable
					batchUpdated={batchUpdated}
					setBatchUpdated={setBatchUpdated}
					handleChangeBlkId={handleChangeBlkId}
					blockIdsByDept={blockIdsByDept}
					allBlockItems={allBlockItems}
					allBlockItemsTA={blockIdsFilteredTA}
					updateClassMutation={updateClassMutation}
					departmentsList={departmentsList}
					handleDepartmentChange={handleDepartmentChange}
					loading={loading}
					isTermActive={isTermActive}
					isAdmin={isAdmin}
					handleRefresh={handleRefresh}
					disabledUI={disabledUI}
					setDisabledUI={setDisabledUI}
					fetchedClassIdsLoading={loading}
					openSelectedClassModal={openSelectedClassModal}
					setOpenSelectedClassModal={setOpenSelectedClassModal}
				/>
			</div>
		</div>
	);
}

// ────────────────────────────────────────────────────────────────────────────────
// OUTER COMPONENT (provides context)
// ────────────────────────────────────────────────────────────────────────────────

export default function ClassAdjustments() {
	const auth = isAuthenticated();
	const token = auth.token;
	const isAdmin = auth.access === "ADMINS";
	const deptIdsFromLDAP = auth.dept_ids;

	// Atoms for cross-component state
	const [selectedDept, setSelectedDept] = useAtom(selectedDeptAtom);
	const [terms, setTerms] = useAtom(termsAtom);
	const [unsavedChanges] = useAtom(unsavedChangesAtom);
	const setSelectedClass = useSetAtom(selectedClassAtom);
	const setCombiningClassesArray = useSetAtom(combiningClassesArrayAtom);
	const setInstanceRows = useSetAtom(instanceRowsAtom);
	const setSelectionModel = useSetAtom(spotOrderingSelectionModelAtom);
	const setUnsavedChanges = useSetAtom(unsavedChangesAtom);

	// Department state
	const [departmentsList, setDepartmentsList] = useState([]);
	const [blockIdsFilteredTA, setBlockIdsFilteredTA] = useState([]);
	const scopeKey = `${selectedDept.deptId || "none"}-${terms.termNumber || "none"}`;
	const previousScopeKeyRef = useRef(scopeKey);

	// ─── QUERIES ───────────────────────────────────────────────────────────────

	const { data: termsList, isFetching: termsLoading } = useTermsQueryFull(token);
	const { data: departments, isFetching: departmentsLoading } = useDepartmentsQuery(token);

	const {
		data: allItemsData,
		isFetching: allItemsDataLoading,
		status: allItemsDataStatus,
	} = useAllItemsQuery(token);

	const {
		data: allBlockItemsData,
		isFetching: allBlockItemsDataLoading,
		status: allBlockItemsStatus,
	} = useBlockItemsQuery(token);

	const { data: allBlockItems, isFetching: allBlockItemsLoading } = useAllBlockItems(
		allBlockItemsStatus === "success" ? allBlockItemsData : null,
		allItemsDataStatus === "success" ? allItemsData : null,
	);

	const {
		data: blockIdsByDept,
		isFetching: blockIdsByDeptLoading,
		status: blockIdsByDeptStatus,
	} = useBlockIdsByDeptQuery(token, selectedDept.deptId, {
		enabled: !!token && !!selectedDept.deptId && !unsavedChanges,
	});

	// Combined loading state for external queries
	const loading =
		termsLoading ||
		departmentsLoading ||
		blockIdsByDeptLoading ||
		allBlockItemsLoading ||
		allItemsDataLoading ||
		allBlockItemsDataLoading;

	// ─── TERM INITIALIZATION ───────────────────────────────────────────────────

	useEffect(() => {
		if (termsList?.length > 0) {
			const termDescriptions = termsList.map((term) => term.desc);
			const termNumbers = termsList.map((term) => term.term);
			const termsListVals = termNumbers.map((term, i) => `${termDescriptions[i]} (${term})`);

			const highestTermNumber = Math.max(...termNumbers);
			const highestTermIndex = termNumbers.indexOf(highestTermNumber);
			const highestTerm = termsListVals[highestTermIndex];

			setTerms({
				terms: termsListVals,
				termValue: highestTerm,
				termNumber: Number(highestTermNumber),
			});
		}
	}, [termsList, setTerms]);

	// ─── DEPARTMENT INITIALIZATION ─────────────────────────────────────────────

	useEffect(() => {
		if (departments?.length > 0) {
			setDepartmentsList(departments.map((dept) => dept.department));
		}
	}, [departments]);

	const [deptManuallySelected, setDeptManuallySelected] = useState(false);

	useEffect(() => {
		if (deptIdsFromLDAP && departments?.length > 0 && !deptManuallySelected) {
			const deptArray = deptIdsFromLDAP.split(",").map((id) => Number(id.trim()));
			const firstDeptId = deptArray[0];
			const dept = departments.find((d) => d.key === firstDeptId);

			if (dept && !deptManuallySelected) {
				setSelectedDept({ department: dept.department, deptId: firstDeptId });
			}
		}
	}, [deptIdsFromLDAP, departments, setSelectedDept, deptManuallySelected]);
	// ─── BLOCK ID FILTERING ────────────────────────────────────────────────────

	useEffect(() => {
		if (
			blockIdsByDept &&
			allBlockItems &&
			blockIdsByDeptStatus === "success" &&
			selectedDept.deptId
		) {
			let blockIdsList = blockIdsByDept;

			// CSJ and CCA departments
			if (selectedDept.deptId === 27 || selectedDept.deptId === 118) {
				blockIdsList = blockIdsByDept.filter((b) => b.dept_id === 27 || b.dept_id === 118);
			} else {
				blockIdsList = blockIdsByDept.filter((b) => b.dept_id !== 27 && b.dept_id !== 118);
			}

			const blockIdsTADict = blockIdsList
				.filter((b) => b.inst_role === "TA" && b.blkid !== "EXCL")
				.reduce((acc, b) => {
					acc[b.blkid] = { forPI: false, forSmallCourse: b.csize === "SMALL" };
					return acc;
				}, {});

			const blockIdsPIDict = blockIdsList
				.filter((b) => b.inst_role === "PI" && b.blkid !== "EXCL")
				.reduce((acc, b) => {
					acc[b.blkid] = { forPI: true, forSmallCourse: b.csize === "SMALL" };
					return acc;
				}, {});

			setBlockIdsFilteredTA({ ...blockIdsTADict, ...blockIdsPIDict });
		}
	}, [blockIdsByDept, allBlockItems, blockIdsByDeptStatus, selectedDept.deptId]);

	// ─── TERM ACTIVE STATE ─────────────────────────────────────────────────────

	const isTermActive = useMemo(() => {
		if (termsList?.length > 0 && terms.termNumber) {
			const term = termsList.find((t) => t.term === terms.termNumber);
			return term?.active === "Y";
		}
		return false;
	}, [termsList, terms.termNumber]);

	// ─── HANDLERS ──────────────────────────────────────────────────────────────

	const handleDepartmentChange = (event) => {
		setDeptManuallySelected(true);
		const deptName = event.target.value;
		const dept = departments.find((d) => d.department === deptName);
		setSelectedDept({ department: deptName, deptId: dept.key });
	};

	useEffect(() => {
		if (previousScopeKeyRef.current === scopeKey) {
			return;
		}

		previousScopeKeyRef.current = scopeKey;
		setSelectedClass(null);
		setCombiningClassesArray([]);
		setInstanceRows([]);
		setSelectionModel(createEmptyRowSelectionModel());
		setUnsavedChanges(false);
	}, [
		scopeKey,
		setSelectedClass,
		setCombiningClassesArray,
		setInstanceRows,
		setSelectionModel,
		setUnsavedChanges,
	]);

	// ─── RENDER ────────────────────────────────────────────────────────────────

	return (
		<SPOTOrderingProvider
			key={scopeKey}
			token={token}
			deptId={selectedDept.deptId}
			termNumber={terms.termNumber}
			blockIdsByDept={blockIdsByDept}
		>
			<SPOTOrderingContent
				isAdmin={isAdmin}
				departmentsList={departmentsList}
				handleDepartmentChange={handleDepartmentChange}
				isTermActive={isTermActive}
				blockIdsByDept={blockIdsByDept}
				blockIdsFilteredTA={blockIdsFilteredTA}
				allBlockItems={allBlockItems}
				loading={loading}
			/>
		</SPOTOrderingProvider>
	);
}
